import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../../config/axiosConfig.js";
import { Panel, PanelHeader, PanelBody, PanelFooter } from "../../components/panel/panel.jsx";
import { useComprobanteById, useDeleteComprobante } from "../../hooks_cache/useComprobantes";
import { DetalleComprobanteTabla } from "./EditableInvoiceTable.js";
import { formatCurrency } from "../../utils/utils_number.js";
import useModalProgreso from "../../components/hooks/useModalProgreso.js";

import { useSelector } from "react-redux";

import usePopupFactura from "./dte_components/hook_dte/usePopupFactura";

import Swal from "sweetalert2";

import ComprobantesRelacionados from "./comprobante_components/ComprobantesRelacionados.js";
import PagosComprobanteTable from "./comprobante_components/PagosComprobanteTable.js";
import StatusBadge from "../../components/StatusBadge";

const Comprobante = () => {
  const navigate = useNavigate();
  const { abrirPopupFacturaByComprobanteId } = usePopupFactura();

  const { comprobante_id } = useParams();

  const { empresas } = useSelector((state) => state.auth);

  const { data: comprobante = {}, refetch, isFetching } = useComprobanteById(comprobante_id);
  const { mutate: deleteComprobante, isLoading } = useDeleteComprobante();

  console.log(comprobante);

  const { start, stop } = useModalProgreso({
    modalTitle: "Enviando comprobante...",
    socketEvents: {
      progress: "dte:progreso",
      error: "dte:error",
    },
  });

  const permitePago = !["05", "06"].includes(comprobante.TipoDTE?.codigo) || false;

  const enviarComprobante = async () => {
    start();
    try {
      const res = await api.get(`/dte/send_dte/${comprobante_id}`);
      console.log(res);
      stop();
    } catch (error) {
      stop();
      console.error("❌ Error al registrar comprobante:", error);
    }
  };

  useEffect(() => {
    if (isLoading || isFetching) {
      Swal.fire({
        title: "Cargando comprobante...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    } else {
      Swal.close();
    }
  }, [isFetching]);

  const showConfirmarInvalidacionModal = () => {
    Swal.fire({
      title: "<strong>Invalidar comprobante</strong>",
      html: "<p>¿Está seguro que desea invalidar este DTE? ¡Esta acción no se puede deshacer!</p>",
      icon: "warning",
      input: "text",
      inputLabel: "Motivo de la invalidación",
      inputPlaceholder: "Ingrese el motivo aquí...",
      showCancelButton: true,
      confirmButtonText: "Sí, estoy seguro.",
      cancelButtonText: "Cancelar",
      showConfirmButton: true,
      allowOutsideClick: true,
      inputValidator: (value) => {
        if (!value) {
          return "Debe ingresar un motivo para continuar.";
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const motivo = result.value;
        invalidarComprobante(motivo); // Pasás el motivo a tu función
      }
    });
  };

  const invalidarComprobante = async (motivo) => {
    try {
      Swal.fire({
        title: "Procesando invalidación...",
        text: "Por favor espera mientras se envía la solicitud a Hacienda",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.post("/dte/invalidarDte", {
        comprobante_id: parseInt(comprobante_id, 10),
        id_empresa: empresas[0].id,
        motivoAnulacion: motivo,
      });

      const { data: resData } = response.data;
      Swal.fire({
        title: "Invalidado!",
        html: `
          <b>Fecha de invalidacion:</b> <span style="white-space: nowrap;">${resData.fhProcesamiento}</span> <br/>
          <b>Código de generación:</b> <span style="white-space: nowrap;">${resData.codigoGeneracion}</span> <br/>
          <b>Mensaje de MH:</b> <span style="white-space: nowrap;">${resData.descripcionMsg}</span> <br/>
          <b>Sello de recibido (Hacienda):</b> <span style="white-space: nowrap;">${resData.selloRecibido}</span> <br/>
        `,
        icon: "success",
      });

      refetch();
    } catch (error) {
      console.error("❌ Error en la petición:", error);

      Swal.fire({
        title: "Error",
        html: `<pre style="text-align: left; white-space: pre-wrap;">${formatearError(error)}</pre>`,
        icon: "error",
        customClass: {
          popup: "text-start",
        },
      });
    }
  };

  const eliminarComprobante = async (comprobante_id) => {
    const res = await Swal.fire({
      title: "Eliminar comprobante",
      text: "¿Está seguro que desea eliminar este comprobante?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (res.isConfirmed) {
      deleteComprobante(comprobante_id, {
        onSuccess: () => {
          Swal.fire("Comprobante eliminado", "", "success");
          navigate(-1);
        },
        onError: () => {
          Swal.fire("Error", "No se pudo eliminar el comprobante", "error");
        },
      });
    }
  };

  const formatearError = (error) => {
    if (!error) return "❌ Error desconocido";

    // Si es string plano
    if (typeof error === "string") return `❌ ${error}`;

    // Si error es un objeto con un campo `error` que contiene la estructura del MH
    const err = error?.error || error;

    // Caso con observaciones del MH
    if (err?.observaciones?.length) {
      return `🚫 ${err.descripcionMsg || error.mensaje || "Error en el documento"}\n\n- ${err.observaciones.join("\n- ")}`;
    }

    // Caso solo con descripcionMsg
    if (err?.descripcionMsg) {
      return `🚫 ${err.descripcionMsg}`;
    }

    // Caso común con mensaje y error como texto
    if (error?.mensaje || err?.message) {
      return `❌ ${error.mensaje || err.message}`;
    }

    // Fallback
    return JSON.stringify(error, null, 2);
  };

  return (
    <div>
      <Panel>
        <PanelHeader noButton={true}>
          {comprobante?.TipoDTE?.descripcion?.toUpperCase() || ""} {" > "} {comprobante?.codigoGeneracion}
        </PanelHeader>
        <PanelBody>
          <div className="row">
            <div className="col-md-6 pe-4 mb-5">
              <div className="row p-1 fs-4">
                <div className="col-md-4 fw-bold ">RAZON SOCIAL: </div>
                <div className="col-md-8">
                  <Link to={`/tercero/${comprobante?.Tercero?.id}`} className="fw-bold text-nowrap">
                    {comprobante?.Tercero?.nombre?.toUpperCase() || "-  "}
                  </Link>
                </div>
              </div>
              <div className="row p-1">
                <div className="col-md-4 fw-bold ">TIPO DE COMPROBANTE: </div>
                <div className="col-md-8">{comprobante?.TipoDTE?.descripcion?.toUpperCase() || ""}</div>
              </div>
              <div className="row p-1">
                <div className="col-md-4 fw-bold ">NUMERO DE CONTROL: </div>
                <div className="col-md-8">{comprobante?.numeroControl}</div>
              </div>

              <div className="row p-1">
                <div className="col-md-4 fw-bold ">FECHA EMISION: </div>
                <div className="col-md-8">{comprobante?.fecEmi}</div>
              </div>
              <div className="row p-1">
                <div className="col-md-4 fw-bold ">DIAS DE CREDITO: </div>
                <div className="col-md-8">{comprobante?.dias_credito}</div>
              </div>
              <div className="row  p-1">
                <div className="col-md-4 fw-bold">FECHA LIMITE PAGO:</div>
                <div className="col-md-8">{comprobante?.fecha_vencimiento}</div>
              </div>
              <div className="row  p-1">
                <div className="col-md-4 fw-bold">OBSERVACIONES:</div>
                <div className="col-md-8">{comprobante?.observaciones}</div>
              </div>
              <div className="shadow fs-11px">
                <DetalleComprobanteTabla data={comprobante?.DetalleComprobantes || []} />
              </div>

              <div className="row mt-4">
                <div className="row p-2 ">
                  <div className="col-md-9 col-8 fw-bold text-end">BASE IMPONIBLE: </div>
                  <div className="col-md-3 col-4 text-end">{formatCurrency(comprobante?.totalGravada)}</div>
                </div>
                <div className="row p-2 ">
                  <div className="col-md-9 col-8 fw-bold text-end">( + ) IMPORTE IVA: </div>
                  <div className="col-md-3 col-4 text-end">{formatCurrency(comprobante?.impuestos)}</div>
                </div>
                <div className="row p-2 ">
                  <div className="col-md-9 col-8 fw-bold text-end">( - ) IMPORTE RETENCION: </div>
                  <div className="col-md-3 col-4 text-end">{formatCurrency(comprobante?.ivaRete1)}</div>
                </div>
                <div className="row p-2 ">
                  <div className="col-md-9 col-8 fw-bold text-end">( + ) IMPORTE EXCENTO: </div>
                  <div className="col-md-3 col-4 text-end">{formatCurrency(comprobante?.totalExenta)}</div>
                </div>
                <div className="row p-2 ">
                  <div className="col-md-9 col-8 fw-bold text-end">( + ) IMPORTE NO SUJETO: </div>
                  <div className="col-md-3 col-4 text-end">{formatCurrency(comprobante?.totalNoSuj)}</div>
                </div>
                <div className="row border-top p-2 fs-4">
                  <div className="col-md-9 col-8 fw-bold text-end">( = ) IMPORTE TOTAL: </div>
                  <div className="col-md-3 col-4 text-end fw-bold">{formatCurrency(Number(comprobante?.totalPagar || 0))}</div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="row mb-4">
                <div className="col-md-12 text-end">
                  <StatusBadge estado={comprobante?.estado} />
                </div>
              </div>

              {comprobante?.comprobantesRelacionados?.length > 0 && (
                <div className="row mb-5">
                  <ComprobantesRelacionados comprobantesRelacionados={comprobante?.comprobantesRelacionados || []} />
                </div>
              )}

              {comprobante?.comprobanteOrigen && (
                <div className="row mb-5">
                  <ComprobantesRelacionados comprobantesRelacionados={[comprobante?.comprobanteOrigen]} />
                </div>
              )}

              {permitePago && (
                <div className="row">
                  <PagosComprobanteTable comprobante={comprobante} />
                </div>
              )}
            </div>
          </div>
        </PanelBody>

        <PanelFooter className="text-center">
          <button className="btn btn-default btn-md ms-5px mb-2" onClick={() => navigate(-1)}>
            Atras
          </button>

          {comprobante?.naturaleza === "venta" && (
            <>
              <Link to={`/venta/registrar_comprobante?comprobanteId=${comprobante_id}`} className="btn btn-primary btn-md ms-5px mb-2">
                Copiar
              </Link>
              <button
                className="btn btn-warning btn-md ms-5px mb-2"
                onClick={() => showConfirmarInvalidacionModal()}
                disabled={comprobante?.estado === "invalidada"}
              >
                Invalidar
              </button>
              <button className="btn btn-indigo btn-md ms-5px mb-2" onClick={() => abrirPopupFacturaByComprobanteId(comprobante_id)}>
                Ver / Imprimir
              </button>
              <button className="btn btn-green btn-md ms-5px mb-2" onClick={() => enviarComprobante()}>
                Enviar por correo
              </button>
            </>
          )}

          {comprobante?.naturaleza === "compra" && (
            <button className="btn btn-red btn-md ms-5px mb-2" onClick={() => eliminarComprobante(comprobante_id)}>
              Eliminar comprobante
            </button>
          )}

          <br />
        </PanelFooter>
      </Panel>
    </div>
  );
};

export default Comprobante;
